
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TestResultDisplay } from "./TestResultDisplay";
import { supabase } from "@/integrations/supabase/client";

const twitterPostTestSchema = z.object({
  tweetLink: z.string().min(1, { message: "Tweet link is required" })
    .url({ message: "Please enter a valid URL" })
    .refine(val => val.includes("twitter.com/") || val.includes("x.com/"), {
      message: "Must be a valid Twitter/X link (twitter.com/... or x.com/...)"
    }),
});

interface TwitterSettingsProps {
  form: UseFormReturn<{
    twitterApiKey?: string;
    twitterApiSecret?: string;
    twitterBearerToken?: string;
    [key: string]: string | undefined;
  }>;
}

export const TwitterSettings = ({ form }: TwitterSettingsProps) => {
  const { toast } = useToast();
  const [testingTwitter, setTestingTwitter] = useState(false);
  const [testingTwitterLink, setTestingTwitterLink] = useState(false);
  const [twitterStatus, setTwitterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [twitterTestResult, setTwitterTestResult] = useState<any>(null);
  const [twitterError, setTwitterError] = useState<string | null>(null);
  const [showRawData, setShowRawData] = useState(false);

  const twitterLinkForm = useForm<z.infer<typeof twitterPostTestSchema>>({
    resolver: zodResolver(twitterPostTestSchema),
    defaultValues: {
      tweetLink: "",
    },
  });

  const testTwitterApi = async () => {
    const apiKey = form.getValues("twitterApiKey");
    const apiSecret = form.getValues("twitterApiSecret");
    const bearerToken = form.getValues("twitterBearerToken");
    
    if (!apiKey || !apiSecret || !bearerToken) {
      toast({
        title: "API Credentials Required",
        description: "Please enter all Twitter API credentials before testing.",
        variant: "destructive",
      });
      return;
    }
    
    setTestingTwitter(true);
    
    try {
      const currentTimeStr = new Date().toISOString();
      
      // Save API Key
      const { data: existingApiKey } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'twitter_api_key')
        .maybeSingle();
      
      if (existingApiKey) {
        await supabase
          .from('integration_settings')
          .update({
            api_key: apiKey,
            updated_at: currentTimeStr
          })
          .eq('integration_name', 'twitter_api_key');
      } else {
        await supabase
          .from('integration_settings')
          .insert({
            integration_name: 'twitter_api_key',
            api_key: apiKey,
            updated_at: currentTimeStr
          });
      }
      
      // Save API Secret
      const { data: existingApiSecret } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'twitter_api_secret')
        .maybeSingle();
      
      if (existingApiSecret) {
        await supabase
          .from('integration_settings')
          .update({
            api_key: apiSecret,
            updated_at: currentTimeStr
          })
          .eq('integration_name', 'twitter_api_secret');
      } else {
        await supabase
          .from('integration_settings')
          .insert({
            integration_name: 'twitter_api_secret',
            api_key: apiSecret,
            updated_at: currentTimeStr
          });
      }
      
      // Save Bearer Token
      const { data: existingBearerToken } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'twitter_bearer_token')
        .maybeSingle();
      
      if (existingBearerToken) {
        await supabase
          .from('integration_settings')
          .update({
            api_key: bearerToken,
            updated_at: currentTimeStr
          })
          .eq('integration_name', 'twitter_bearer_token');
      } else {
        await supabase
          .from('integration_settings')
          .insert({
            integration_name: 'twitter_bearer_token',
            api_key: bearerToken,
            updated_at: currentTimeStr
          });
      }
      
      // Also sync the token to the edge function environment
      await supabase.functions.invoke('sync-twitter-bearer-token', {
        body: {}
      });
      
      setTwitterStatus('success');
      toast({
        title: "Twitter API Settings Saved",
        description: "Your Twitter API credentials have been saved and synced.",
      });
      
    } catch (error) {
      console.error("Error saving Twitter settings:", error);
      setTwitterStatus('error');
      toast({
        title: "Twitter API Test Failed",
        description: "Unable to save Twitter settings.",
        variant: "destructive",
      });
    } finally {
      setTestingTwitter(false);
    }
  };

  const testTwitterLink = async (data: z.infer<typeof twitterPostTestSchema>) => {
    setTestingTwitterLink(true);
    setTwitterTestResult(null);
    setTwitterError(null);
    
    try {
      // First sync the bearer token
      toast({
        title: "Syncing Twitter Credentials",
        description: "Syncing your Twitter bearer token to the edge function...",
      });
      
      await supabase.functions.invoke('sync-twitter-bearer-token', {
        body: {}
      });
      
      // Then test the Twitter link
      toast({
        title: "Testing Twitter Link",
        description: "Fetching metrics for the provided URL...",
      });
      
      const response = await supabase.functions.invoke('twitter-track-metrics', {
        body: {
          postLink: data.tweetLink
        },
      });
      
      const result = response.data;
      
      if (response.error || result?.error) {
        setTwitterError(result?.details || result?.error || response.error?.message || "Unknown error occurred");
        toast({
          title: "Twitter API Error",
          description: result?.details || result?.error || response.error?.message || "Unknown error occurred",
          variant: "destructive",
        });
      } else {
        setTwitterTestResult(result);
        
        toast({
          title: "Twitter Post Link Processed",
          description: result?.message || `Processed tweet data with metrics.`,
          variant: "default",
        });
      }
    } catch (error: any) {
      setTwitterError(error.message || "An error occurred while processing the Twitter post link.");
      toast({
        title: "Error Processing Link",
        description: error.message || "An error occurred while processing the Twitter post link.",
        variant: "destructive",
      });
    } finally {
      setTestingTwitterLink(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Twitter/X API</CardTitle>
        <CardDescription>
          Configure your Twitter API integration for tracking metrics. 
          <a 
            href="https://developer.twitter.com/en/portal/dashboard" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline ml-1"
          >
            Get credentials here
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="twitterApiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Twitter API Key</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Enter your Twitter API Key" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="twitterApiSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Twitter API Secret</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Enter your Twitter API Secret" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="twitterBearerToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Twitter Bearer Token</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Enter your Twitter Bearer Token" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
      </CardContent>
      <CardFooter className="flex flex-col items-start space-y-4">
        <Button 
          variant="default" 
          onClick={testTwitterApi} 
          disabled={testingTwitter}
        >
          {testingTwitter ? "Testing..." : "Save Twitter Settings"}
          {twitterStatus === 'success' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
          {twitterStatus === 'error' && <AlertCircle className="ml-2 h-4 w-4 text-red-500" />}
        </Button>
        
        <div className="w-full">
          <h3 className="text-sm font-medium mb-2">Test Twitter Link</h3>
          
          <div className="border border-border rounded-md p-4">
            <Form {...twitterLinkForm}>
              <form onSubmit={twitterLinkForm.handleSubmit(testTwitterLink)} className="space-y-4">
                <FormField
                  control={twitterLinkForm.control}
                  name="tweetLink"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tweet Link</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., https://twitter.com/username/status/123456789" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button type="submit" disabled={testingTwitterLink}>
                  {testingTwitterLink ? "Processing..." : "Test Link"}
                </Button>
              </form>
            </Form>
          </div>
          
          <TestResultDisplay
            result={twitterTestResult}
            error={twitterError}
            showRawData={showRawData}
            onToggleRawData={() => setShowRawData(!showRawData)}
            title="Twitter API Response"
          />
        </div>
      </CardFooter>
    </Card>
  );
};
